<script setup>
import {
    state,
    removeFromBasket,
    removeVoucherFromBasket,
    fetchBookingPanel,
    getBasketCheckoutUrl,
    basketExpiresInSeconds,
} from '@/shared/basketStore.js';
import BasketSummary from '@/components/BasketSummary.vue';
import CalendarBookingPanel from '@/search/components/CalendarBookingPanel.vue';
import { openPanel } from '@/shared/panelStack.js';

// The basket as a pop-out panel entry (pushed onto shared/panelStack.js). Exists purely
// to connect the store to BasketSummary.vue.
//
// Panel entries carry their props captured at open time, but the basket refetches after
// every add and remove, so passing its contents as entry props would freeze them at
// whatever they were when the panel opened. Reading the store here instead keeps
// BasketSummary presentational, with the same props and emit it has always had, which
// matters because its styling is still placeholder and it has its own port coming.

/**
 * Reopen the booking panel against one basket row, so its spa, guest count or date can be
 * changed. See docs/feature-requests/checkout-location-date-editing-scope-technical.md §11.
 *
 * openPanel(), not replacePanelContent(): the booking panel STACKS on top of the basket
 * here, unlike the add flow which replaces it. Adding commits something and leaves nothing
 * to come back to; an edit can be abandoned, and closing it should put the customer back in
 * the basket they were looking at rather than nowhere.
 *
 * No stage is named, so the panel opens with every card collapsed showing what is currently
 * chosen. The row has one control for all three, unlike the checkout summary's card-by-card
 * pencils, so there is nothing here that says which one the customer came for.
 */
async function onEdit(guid) {
    const panel = await fetchBookingPanel(guid);

    // fetchBookingPanel() has already put the reason on the store, and BasketSummary is
    // still on screen to show it.
    if (! panel) return;

    openPanel({
        component: CalendarBookingPanel,
        title: panel.product.name,
        props: {
            product:             panel.product,
            locations:           panel.locations,
            locationId:          panel.locationId,
            initialMonth:        panel.initialMonth,
            initialPeople:       panel.initialPeople,
            availabilityApiUrl:  panel.apiUrls.availability,
            spaBreakRoomsApiUrl: panel.apiUrls.spaBreakRooms,
            variantsApiUrl:      panel.apiUrls.bookingVariants,

            basketItemGuid:   guid,
            initialDate:      panel.initialDate,
            initialVariantId: panel.initialVariantId,
            // Default 'basket': success swaps back to this panel rather than reloading the
            // page, since nothing on the page behind it was built from this item.
        },
    });
}
</script>

<template>
    <!-- The countdown is read at render, not taken from the basket payload. The payload's own
         figure was true when it was fetched, and this panel unmounts when the basket is closed,
         so a fresh mount minutes later would restart the countdown from that stale number. -->
    <BasketSummary
        :items="state.basket.items"
        :vouchers="state.basket.vouchers ?? []"
        :total="state.basket.total"
        :discount="state.basket.discount ?? 0"
        :discount-code="state.basket.discountCode ?? 0"
        :discount-code-name="state.basket.discountCodeName ?? null"
        :member-discount="state.basket.memberDiscount ?? 0"
        :payable="state.basket.payable ?? 0"
        :error="state.basketError"
        :notice="state.basketNotice"
        :checkout-url="getBasketCheckoutUrl()"
        :has-expiry="state.basket.hasExpiry ?? false"
        :expires-in-seconds="basketExpiresInSeconds()"
        @remove="removeFromBasket"
        @remove-voucher="removeVoucherFromBasket"
        @edit="onEdit"
    />
</template>
